import { Formik } from 'formik';
import { GraphQLError } from 'graphql';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Box } from 'reflexbox';
import * as yup from 'yup';

import * as graphql from '@treams/treams-network';
import {
	Button,
	Callout,
	GraphQLErrorMessage,
	Input,
	Label,
	Subheading,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import BareLayout from '../../../../../../src/packages/kakapo-ui-application/layouts/BareLayout';
import PublicForm from '../../components/authentication/PublicForm';

export default function ForgotPassword() {
	const { addToast } = useToasts();
	const { t } = useTranslation();
	const history = useHistory();

	const [commit] = graphql.useConfirmForgotPasswordMutation();

	const [error, setError] = useState<GraphQLError | undefined>(undefined);
	const [success, setSuccess] = useState(false);

	const params = new URLSearchParams(window.location.search.substring(1));

	const [timeoutHandle, setTimeoutHandle] = useState<NodeJS.Timeout>();

	const validationSchema = yup.object({
		login: yup.string().trim().max(128).required(t('common.requestNewPassword.loginRequired')),
		code: yup.string().trim().required(t('common.resetPassword.codeRequired')),
		password: yup.string().required(t('common.passwordValidation.required')),
	});

	// Make sure that the user isn't redirected after they already manually
	// navigated to another page.
	useEffect(() => {
		return () => clearTimeout(timeoutHandle);
	}, [timeoutHandle]);

	return (
		<BareLayout>
			<Formik
				initialValues={{
					login: params.get('username') ?? '',
					code: params.get('code') ?? '',
					password: '',
				}}
				validationSchema={validationSchema}
				onSubmit={async ({ login, code, password }) => {
					try {
						await commit({
							variables: {
								input: {
									username: login.trim(),
									confirmationCode: code.trim(),
									newPassword: password,
								},
							},
						});

						setSuccess(true);

						addToast(t('common.resetPassword.success'), {
							appearance: 'success',
							autoDismiss: true,
						});

						clearTimeout(timeoutHandle);

						setTimeoutHandle(
							setTimeout(() => {
								history.push('/authentication');
							}, 10000),
						);
					} catch (errors) {
						setError(errors.graphQLErrors[0]);
					}
				}}
			>
				{({ errors, touched }) => (
					<PublicForm>
						<Subheading mb={`${tokens.gap.section}px`}>
							{t('common.resetPassword.title')}
						</Subheading>

						<GraphQLErrorMessage error={error} />

						{success ? (
							<Callout color="employee" description={t('common.resetPassword.success')} />
						) : (
							<>
								<Box mb={3}>
									<Label mb={2}>{t('common.userName')}:</Label>
									<Input.Formik name="login" disabled />
								</Box>

								<Box mb={3}>
									<Label mb={2}>{t('common.code')}:</Label>
									<Input.Formik name="code" disabled />
								</Box>

								<Box mb={3}>
									<Label mb={2}>{t('common.newPassword')}:</Label>
									<Input.Formik name="password" type="password" autoFocus />
									{touched.password && errors.password && (
										<Callout mt={2} color="red">
											{errors.password}
										</Callout>
									)}
								</Box>

								<Button.Formik
									title={t('common.resetPassword.button')}
									disabled={Boolean(errors.login || errors.code || errors.password)}
								/>
							</>
						)}
					</PublicForm>
				)}
			</Formik>
		</BareLayout>
	);
}
